import { useRouter } from 'next/router';
import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Index, Hits, Configure, useStats } from 'react-instantsearch';
import Arrow from '@monofront/design-system/visuals/Icons/Old/Arrow';
import Search from '@monofront/design-system/visuals/Icons/Search';
import ListProduct from './ListProduct';
import ListCollection from './ListCollection';
import ListBrand from './ListBrand';
import getConfig from 'next/config';
import { useTranslations } from 'next-intl';
import {
  generateCollectionIndexName,
  generateProductIndexName,
  getChannelFromLocale,
  useAutocomplete,
} from '@monofront/business/utils';
import {
  ALGOLIA_PRODUCT_SORT_BY,
  BRANDS_SEARCH_RESULTS,
  COLLECTION_TYPE_FILTER,
} from '@monofront/business/constants';
import { trackEvent } from '@monofront/tracking';
import {
  ALGOLIA_CONTEXT_AUTOCOMPLETE_V2,
  useMediaQuery,
} from '@monofront/business/hooks';
import { useGlobalSearchContext } from '@context/GlobalSearchContext';
import Link from 'next/link';
import { LayoutContext } from '@context/LayoutContext';
import { useFeatureIsOn } from '@monofront/business/contexts';
import { useEnrichAlgoliaContexts } from '@hooks/businessContainer';
import InputIcon from '@monofront/design-system/components/Form/InputIcon';
import SearchPanel from '../SearchPanel';
import { useInstantSearch } from 'react-instantsearch-core';
import { useDocumentTouchMoveThrottled } from '@hooks/layout/useDocumentEventThrottled';
import { useLayoutRefs } from '@context/layoutRefs';
import { useSearchPanelContext } from '@context/SearchPanelContext';
import { useNavigationEventListener } from '@hooks/useNavigationEventListener';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';

const {
  publicRuntimeConfig: { algoliaConfig },
} = getConfig();

const CollectionsResults = ({ children }) => {
  const { results } = useInstantSearch();

  const hasResults = results && results.nbHits !== 0;
  const tCatalog = useTranslations('Catalog');

  if (!hasResults) {
    return <p>{tCatalog('noShelveFound')}</p>;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const BrandsResults = ({ children }) => {
  const { results } = useInstantSearch();

  const hasResults = results && results.nbHits !== 0;
  const tCatalog = useTranslations('Catalog');

  if (!hasResults) {
    return <p>{tCatalog('noBrandFound')}</p>;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const ProductsResults = ({ children }) => {
  const { results } = useInstantSearch();

  const hasResults = results && results.nbHits !== 0;
  const tCatalog = useTranslations('Catalog');

  if (!hasResults) {
    return <p>{tCatalog('noProductFound')}</p>;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const TotalProductsFoundResults = () => {
  const { nbHits } = useStats();

  const hasResults = nbHits !== 0;
  const tCatalog = useTranslations('Catalog');

  if (!hasResults) {
    return null;
  }

  return (
    <>
      {tCatalog('seeAllFoundProducts', {
        nbProductsFound: nbHits,
      })}
    </>
  );
};

interface IGlobalSearchProps {
  handleInputFocus?: () => void;
  handleInputBlur?: () => void;
}

const Autocomplete: React.FC<IGlobalSearchProps> = ({
  handleInputFocus: handleInputFocusAnimations,
  handleInputBlur: handleInputBlurAnimations,
}) => {
  const { currentRefinement, refine, indices } = useAutocomplete();
  const isMobile = useMediaQuery(800);

  const { isGlobalSearchResultsPanelOpen, setIsGlobalSearchResultsPanelOpen } =
    useGlobalSearchContext();
  const { mainHeaderHeight } = useContext(LayoutContext);
  const { headerSearchAutocompleteRef } = useLayoutRefs();
  const router = useRouter();
  const tCatalog = useTranslations('Catalog');
  const productIndexName = generateProductIndexName(
    algoliaConfig.environment,
    false,
    getChannelFromLocale(router.locale),
    true,
    ALGOLIA_PRODUCT_SORT_BY.POPULARITY,
  );
  const isBrandResultsOn = useFeatureIsOn(BRANDS_SEARCH_RESULTS);
  // used to debounce the refinement and the display of the search
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const inputIconRef = useRef<HTMLInputElement>(null);
  const contexts = useEnrichAlgoliaContexts([ALGOLIA_CONTEXT_AUTOCOMPLETE_V2]);
  const { searchPanel } = useSearchPanelContext();

  const nbProductsFound =
    indices.find(({ indexName }) => indexName === productIndexName)?.hits
      .length || 0;

  const hasSearch = currentRefinement !== '';

  const noResultsFound =
    hasSearch && indices.every(({ hits }) => hits?.length === 0);

  const openPanel = () => {
    setIsGlobalSearchResultsPanelOpen(true);
  };

  const closePanel = () => {
    setIsGlobalSearchResultsPanelOpen(false);
  };

  const blurInput = () => {
    inputIconRef.current?.blur();
    handleInputBlurAnimations?.();
  };

  const handleClosePanel = () => {
    closePanel();
    blurInput();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(() => {
      refine(event.target.value);

      if (!isMobile) {
        // Desktop specific behaviour
        if (!isGlobalSearchResultsPanelOpen) {
          openPanel();
        }

        if (event.target.value.length === 0 && !searchPanel) {
          closePanel();
        }
      }
    }, 250);
  };

  const handleFocus = () => {
    handleInputFocusAnimations?.();

    if (isMobile || (!isMobile && searchPanel)) {
      openPanel();
    }
  };

  const handleClear = () => {
    inputIconRef.current!.value = '';
    refine('');

    if (isMobile || searchPanel) {
      openPanel();
    } else {
      closePanel();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      refine(e.target.value);
      e.preventDefault();
      handleSearch(e.target.value);
    }
  };

  const handleSearch = (
    value?: React.MouseEvent<HTMLDivElement | HTMLButtonElement> | string,
  ) => {
    router
      .push({
        pathname: '/search',
        query: { query: typeof value === 'string' ? value : currentRefinement },
      })
      .then(() => {
        handleClosePanel();
        clearTimeout(timeoutIdRef.current);
      });
  };

  const handleTouchMove = (event: TouchEvent) => {
    const isSearchInputFocused =
      document.activeElement === inputIconRef.current;

    if (isSearchInputFocused) {
      event.preventDefault();
      event.stopPropagation();
      inputIconRef.current?.blur();
    }
  };

  useDocumentTouchMoveThrottled(handleTouchMove);

  useNavigationEventListener({ cb: () => handleClosePanel() });

  useEffect(() => {
    // this effects is required to handle panel being closed elsewhere in the tree (thanks to context - ie on result card click)
    // we need to make sure animations are correctly triggered
    if (!isGlobalSearchResultsPanelOpen && isMobile) {
      handleClosePanel();
    }
  }, [isGlobalSearchResultsPanelOpen, handleClosePanel, isMobile]);

  useEffect(() => {
    if (!currentRefinement || router.query.query !== '') {
      return;
    }

    setIsGlobalSearchResultsPanelOpen(true);

    return () => {
      clearTimeout(timeoutIdRef.current);
    };
  }, [currentRefinement, router.query.query]);

  useEffect(() => {
    // clear input on navigation or re-set input search query string when coming back on search results page
    if (router.pathname === '/search') {
      const value = (router.query.query as string) ?? '';

      refine(value);
      inputIconRef.current!.value = value;
    } else {
      refine('');
      inputIconRef.current!.value = '';
    }
  }, [router.pathname, router.query.query, refine]);

  const [panelContent, setPanelContent] = useState<
    'no-results' | 'search-results' | 'default' | 'blank'
  >('blank');

  useEffect(() => {
    const newPanelContent = (() => {
      if (noResultsFound) {
        return 'no-results';
      }

      if (hasSearch) {
        return 'search-results';
      }

      if (searchPanel && isGlobalSearchResultsPanelOpen) {
        return 'default';
      }

      return 'blank';
    })();

    if (newPanelContent === panelContent) {
      return;
    }

    switch (newPanelContent) {
      case 'default':
        trackEvent({
          type: 'LFSearchRecommendationOpen',
        });
        break;
      case 'search-results':
        trackEvent({
          type: 'LFSearchInputType',
        });
        break;
      default:
        break;
    }

    setPanelContent(newPanelContent);
  }, [noResultsFound, hasSearch, searchPanel, isGlobalSearchResultsPanelOpen]);

  return (
    <>
      {isGlobalSearchResultsPanelOpen &&
        (currentRefinement?.length ?? 0) >= 0 && (
          <div className="transparent-bg desktop-only" onClick={closePanel} />
        )}
      <div className="autocomplete-container" ref={headerSearchAutocompleteRef}>
        <div className="back-button-and-input">
          <button
            className="mobile-only tw-lf-button-clear"
            onClick={() => {
              handleClosePanel();
            }}
            type="button"
            data-testid="GlobalSearchBack-button"
          >
            <Arrow headDirection="left" width={9} height={15} />
          </button>
          <InputIcon
            defaultValue={
              typeof router.query.query === 'string' ? router.query.query : ''
            }
            withIconPointer
            rounded
            placeholder={tCatalog('searchPlaceholder')}
            type="search"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClickIcon={handleSearch}
            onClickIconDisabled={!hasSearch}
            onFocus={handleFocus}
            register={inputIconRef}
            withColoredBorder
            onClear={handleClear}
            withClearIcon={Boolean(currentRefinement?.length)}
          >
            <Search size={IconSizes.md} />
          </InputIcon>
        </div>
        <div className="global-search-results-panel">
          {panelContent === 'blank' && (
            <div className="mobile-only tw-h-full tw-w-full tw-bg-white" />
          )}
          {panelContent === 'default' && <SearchPanel />}
          {panelContent === 'no-results' && (
            <p className="limited-width-container no-results-found">
              {tCatalog('noResultsFound')}
            </p>
          )}
          {panelContent === 'search-results' && (
            <div className="limited-width-container results">
              <div className="brands-and-collections">
                <div className="collections">
                  <h2 className="hxm">{tCatalog('shelves')}</h2>
                  <Index
                    indexName={generateCollectionIndexName(
                      algoliaConfig.environment,
                      router.locale,
                    )}
                    indexId="instant_search_collections"
                  >
                    <Configure
                      clickAnalytics
                      hitsPerPage={4}
                      filters={COLLECTION_TYPE_FILTER.SECTION}
                    />
                    <CollectionsResults>
                      <Hits hitComponent={ListCollection} />
                    </CollectionsResults>
                  </Index>
                </div>
                {isBrandResultsOn && (
                  <div className="brands">
                    <h2 className="hxm">{tCatalog('brands')}</h2>
                    <Index
                      indexName={generateCollectionIndexName(
                        algoliaConfig.environment,
                        router.locale,
                      )}
                      indexId="instant_search_vendors"
                    >
                      <Configure
                        clickAnalytics
                        hitsPerPage={4}
                        filters={COLLECTION_TYPE_FILTER.VENDOR}
                      />
                      <BrandsResults>
                        <Hits hitComponent={ListBrand} />
                      </BrandsResults>
                    </Index>
                  </div>
                )}
              </div>
              <div className="products">
                <div className="products-title-and-link">
                  <h2 className="hxm">{tCatalog('products')}</h2>
                  {nbProductsFound ? (
                    <Link
                      href={`/search?query=${currentRefinement}`}
                      legacyBehavior
                    >
                      <a
                        data-testid="Autocomplete-go-to-see-all-products"
                        className="tw-lf-link-md"
                        onClick={handleClosePanel}
                      >
                        <TotalProductsFoundResults />
                      </a>
                    </Link>
                  ) : null}
                </div>
                <Index indexName={productIndexName}>
                  <Configure
                    clickAnalytics
                    hitsPerPage={6}
                    ruleContexts={contexts}
                  />
                  <ul className="products-list">
                    <ProductsResults>
                      <Hits hitComponent={ListProduct} />
                    </ProductsResults>
                  </ul>
                </Index>
              </div>
            </div>
          )}
        </div>
      </div>
      <style jsx global>{`
        @media (max-width: 555px) {
          body {
            overflow: ${isGlobalSearchResultsPanelOpen ? 'hidden' : 'unset'};
          }
        }
      `}</style>
      <style jsx>{`
        .autocomplete-container {
          max-width: 700px;
          width: 100%;
        }

        .transparent-bg {
          background-color: var(--clr-overlay);
          padding: var(--padding-layout-base);
          position: fixed;
          margin: auto;
          width: 100%;
          height: 100%;
          left: 0;
          top: ${mainHeaderHeight + 'px'};
        }

        .global-search-results-panel {
          width: 100%;
          position: absolute;
          /* prevent useless algolia call on first search : need to keep this div in DOM */
          display: ${isGlobalSearchResultsPanelOpen ? 'block' : 'none'};
          background: var(--white);
          border-top: 1px solid var(--clr-tertiary-2);
          overflow: auto;
          margin: auto;
          left: 0;
          margin-top: calc(var(--space-4) - 1px);
          padding-bottom: ${nbProductsFound
            ? 'var(--space-2)'
            : 'var(--space-6)'};
          min-height: 92px;
          max-height: 600px;
          box-shadow: 0px 2px 16px 0px rgba(0, 0, 0, 0.06);
          clip-path: inset(0 -100% -100% -100%);
        }

        .no-results-found {
          display: flex;
          justify-content: center;
          margin-top: var(--space-6);
          text-align: center;
        }

        .results {
          display: ${isGlobalSearchResultsPanelOpen ? 'grid' : 'none'};

          grid-template-columns: 282px calc(100% - 282px);
          padding-top: var(--space-6);
          justify-content: space-between;
        }

        .brands-and-collections {
          width: 282px;
          margin-bottom: ${nbProductsFound ? 'var(--space-6)' : '0'};
          padding-right: var(--space-5);
        }

        .brands-and-collections .hxm {
          margin-bottom: var(--space-4);
        }

        .brands {
          margin-top: var(--space-6);
        }

        .products {
          max-width: 858px;
          width: 100%;
        }

        .products-title-and-link {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          margin-bottom: var(--space-4);
        }

        .collections > :global(.ais-Hits > .ais-Hits-list > .ais-Hits-item),
        .brands > :global(.ais-Hits > .ais-Hits-list > .ais-Hits-item) {
          list-style-type: none;
        }

        .products-list > :global(.ais-Hits > .ais-Hits-list) {
          display: grid;
          grid-template-columns: repeat(2, calc(52% - (var(--space-7) / 2)));
          column-gap: var(--space-2);
        }

        .products-list > :global(.ais-Hits > .ais-Hits-list > .ais-Hits-item) {
          margin-bottom: var(--space-2);
        }

        @media (max-width: 1000px) {
          .products-list > :global(.ais-Hits > .ais-Hits-list) {
            display: grid;
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 800px) {
          .autocomplete-container {
            margin: ${isGlobalSearchResultsPanelOpen ? 0 : 'var(--space-2) 0'};
            margin-top: 0;
            max-width: 100%;
            height: ${isGlobalSearchResultsPanelOpen ? '60px' : 'initial'};
          }

          .back-button-and-input {
            display: flex;
            flex-direction: row;
          }

          .back-button-and-input button {
            margin-right: var(--space-4);
            display: ${isGlobalSearchResultsPanelOpen ? 'block' : 'none'};
          }

          .autocomplete-container :global(input) {
            height: 42px;
          }

          .back-button-and-input :global(label) {
            width: 100%;
          }

          ${isGlobalSearchResultsPanelOpen
            ? `
          .autocomplete-container {
            position: absolute;
            background-color: white;
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
          }

          .back-button-and-input {
            top: 0;
            margin: var(--space-2) var(--space-4);
          }
          `
            : ''}

          .global-search-results-panel {
            position: absolute;
            left: 0;
            right: 0;
            width: 100%;
            height: calc(100vh - 59px);
            max-height: none;
            margin-top: 0;
            padding: 0;
          }

          .no-results-found {
            margin-top: var(--space-4);
          }

          .results {
            padding: var(--space-4);
            grid-template-columns: 100%;
          }

          .products-list {
            margin-bottom: var(--space-8);
          }

          .products-list > :global(.ais-Hits > .ais-Hits-list) {
            grid-template-columns: 100%;
          }
        }

        @media (max-width: 555px) {
          .products-list
            > :global(.ais-Hits > .ais-Hits-list > .ais-Hits-item) {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default Autocomplete;
